import { useEffect, useState } from 'react';
import { Eye, Info, Mail } from 'lucide-react';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { projectId } from '../utils/supabase/info';
import { INVITATION_VARIABLES, renderInvitation } from '../utils/clientIntake';

/**
 * Add Client › Invitation email (RESTORE_PLAN R3.4, carrying the old
 * ConsultantPanel's editor): start from one of the firm's invitation templates
 * (Firm › Settings › Email templates) or the standard email, edit the message
 * with {{variables}}, and preview it. The server sends it to the new admin in
 * place of the standard welcome, with the set-password button.
 */

export interface InvitationDraft {
  templateId: string;
  body: string;
}

interface EmailTemplate {
  id: string;
  name: string;
  body: string;
  type: string;
  isDefault?: boolean;
}

export const STANDARD = 'default';

/** The firm's templates that fit a client invitation, the firm's default first. */
export function invitationTemplates(templates: EmailTemplate[]): EmailTemplate[] {
  return templates
    .filter((t) => t.type === 'client_invitation' || t.type === 'custom')
    .sort((a, b) => Number(!!b.isDefault && b.type === 'client_invitation') - Number(!!a.isDefault && a.type === 'client_invitation'));
}

interface InvitationEmailEditorProps {
  accessToken: string;
  value: InvitationDraft;
  onChange: (next: InvitationDraft) => void;
  /** Values for the preview (the link is shown as a placeholder: the real one is made when the email is sent). */
  preview: { companyName: string; adminName: string; consultantName: string; consultantEmail: string; firmName: string };
}

export function InvitationEmailEditor({ accessToken, value, onChange, preview }: InvitationEmailEditorProps) {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/email-templates`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const body = res.ok ? await res.json() : { templates: [] };
        if (!live) return;
        const list = invitationTemplates(body.templates || []);
        setTemplates(list);
        // The firm's default invitation is where a new client starts, as in the old form
        const firmDefault = list.find((t) => t.isDefault && t.type === 'client_invitation');
        if (firmDefault && value.templateId === STANDARD && !value.body) onChange({ templateId: firmDefault.id, body: firmDefault.body });
      } catch {
        if (live) setTemplates([]);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (id: string) => {
    if (id === STANDARD) onChange({ templateId: STANDARD, body: '' });
    else onChange({ templateId: id, body: templates.find((t) => t.id === id)?.body ?? '' });
  };

  const rendered = renderInvitation(value.body, { ...preview, loginLink: '[Set your password link]' });

  return (
    <div className="space-y-4" data-testid="invitation-email-editor">
      <div className="flex items-center gap-2">
        <Mail className="size-4 text-blue-500" aria-hidden="true" />
        <Label>Invitation email</Label>
      </div>
      <p className="text-muted-foreground">The email the new admin receives. Leave the message empty to send the standard welcome.</p>

      <div className="space-y-2">
        <Label htmlFor="invitation-template">Start from</Label>
        <Select value={value.templateId} onValueChange={pick}>
          <SelectTrigger id="invitation-template" aria-label="Start from">
            <SelectValue placeholder={loading ? 'Loading templates...' : 'Select template'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={STANDARD}>Standard welcome</SelectItem>
            {templates.map((t) => (
              <SelectItem key={t.id} value={t.id}>{t.name}{t.isDefault ? ' (default)' : ''}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="invitation-body">Message</Label>
        <Textarea
          id="invitation-body"
          rows={8}
          value={value.body}
          onChange={(e) => onChange({ ...value, body: e.target.value })}
          placeholder={'Hi {{adminName}},\n\nWelcome to {{firmName}}. Your portal for {{companyName}} is ready.\n\nGet started here: {{loginLink}}\n\nBest regards,\n{{consultantName}}'}
        />
        <Alert>
          <Info className="size-4" />
          <AlertDescription>Available variables: {INVITATION_VARIABLES.map((v) => `{{${v}}}`).join(', ')}</AlertDescription>
        </Alert>
      </div>

      {value.body.trim() && (
        <div className="space-y-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setShowPreview(!showPreview)}>
            <Eye className="size-4 mr-2" aria-hidden="true" />
            {showPreview ? 'Hide preview' : 'Preview'}
          </Button>
          {showPreview && (
            <pre className="whitespace-pre-wrap rounded-md border bg-gray-50 p-4 font-sans" data-testid="invitation-preview">{rendered}</pre>
          )}
        </div>
      )}
    </div>
  );
}
